import { computed, ref } from 'vue'
import type { DrawerContext } from './drawerTypes'
import type { CanvasNodeContext } from '../next/composables/salesPlayCanvasContext'
import type { IntegrationContext } from '../next/composables/integrationContext'
import type { DialerSettingsContext } from '../next/composables/dialerSettingsContext'

type AnyContext = DrawerContext | CanvasNodeContext | IntegrationContext | DialerSettingsContext

const _context = ref<AnyContext | null>(null)
const _stable = ref<AnyContext | null>(null)
const _dismissed = ref(false)
const _dismissedByUser = ref(false)
const _pinned = ref(false)
const _emptyText = ref('')

let _debounceTimer: ReturnType<typeof setTimeout> | null = null
const SETTLE_MS = 100

/**
 * Generation counter — increments on every setContext call (before debounce).
 * Cards can snapshot this value before an async fetch and compare afterward
 * to discard stale responses that arrive after the user navigated away.
 */
const _generation = ref(0)

/** Bumps when a contact's background /enrich finished; cards fed by that pipeline reload on it. */
const _contactEnrichedTick = ref(0)

export function useContextDrawer() {
  /**
   * drawerContext — updates immediately on every setContext call.
   * Use for lightweight/synchronous UI (row highlighting, v-if, etc.)
   */
  const drawerContext = computed(() => _context.value)

  /**
   * stableContext — debounced. Only updates after selection settles.
   * Use for anything that triggers API fetches (card components, account basics).
   */
  const stableContext = computed(() => _stable.value)

  /**
   * contextGeneration — reactive counter that increments on every setContext.
   * Use to discard stale async results in card components.
   */
  const contextGeneration = computed(() => _generation.value)

  const contactEnrichedTick = computed(() => _contactEnrichedTick.value)
  function noteContactEnriched() {
    _contactEnrichedTick.value++
  }

  const drawerOpen = computed(() => !_dismissed.value)

  const drawerPresent = computed(() => _pinned.value || _context.value !== null)
  const drawerEmptyText = computed(() => _emptyText.value)

  function pinDrawer(emptyText = '') {
    _pinned.value = true
    _emptyText.value = emptyText
  }

  function unpinDrawer() {
    _pinned.value = false
    _emptyText.value = ''
  }

  function setContext(ctx: AnyContext | null) {
    // Don't refetch when we call the drawer again on the same context
    if (JSON.stringify(_context.value) === JSON.stringify(ctx)) return

    _context.value = ctx
    _generation.value++
    if (_debounceTimer) clearTimeout(_debounceTimer)
    _debounceTimer = setTimeout(() => {
      _stable.value = ctx
      _debounceTimer = null
    }, SETTLE_MS)
  }

  /** Did the user themselves close it? */
  const drawerDismissedByUser = computed(() => _dismissedByUser.value)

  function closeDrawer(opts?: { byUser?: boolean }) {
    _dismissed.value = true
    if (opts?.byUser) _dismissedByUser.value = true
  }

  function openDrawer() {
    _dismissed.value = false
    _dismissedByUser.value = false
  }

  function resetDismissedByUser() {
    _dismissedByUser.value = false
  }

  return {
    drawerContext,
    stableContext,
    contextGeneration,
    contactEnrichedTick,
    drawerOpen,
    drawerPresent,
    drawerEmptyText,
    drawerDismissedByUser,
    setContext,
    noteContactEnriched,
    pinDrawer,
    unpinDrawer,
    closeDrawer,
    openDrawer,
    resetDismissedByUser,
  }
}
